import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  Field,
  Metric,
  Select,
  TextInput,
  TextArea,
  formatDate,
} from "@odingard/design-system";
import type {
  AuditFinding,
  AuditPackageLink,
  AuditProject,
  AuditProjectStatus,
  AuditScopeEntry,
  AuditScopeType,
  ComplianceFramework,
  CoverageRollup,
  EvidencePackage,
  FindingSeverity,
  FindingTransition,
  FrameworkRequirement,
  FrameworkVersion,
  ReadinessComponent,
  ReadinessScore,
  ReadinessSnapshot,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, auditApi, evidenceApi, frameworksApi } from "../../lib/api";
import {
  ActionGate,
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import {
  FINDING_STATUSES,
  FindingStatusBadge,
  NEXT_FINDING_STATUS,
  NEXT_PROJECT_STATUS,
  ProjectStatusBadge,
  SEVERITIES,
  SeverityBadge,
  componentRatioLabel,
  findingStatusLabel,
  isFreeReference,
  percent,
  projectStatusLabel,
  scopeTypeLabel,
  severityLabel,
} from "./auditLabels";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

// One audit (APP-004 R3 P5): scope, findings, coverage, readiness, snapshots
// and the evidence packages handed over.
//
// Three rules belong to the backend and are shown, never predicted, here. A
// finding's own owner cannot close it during fieldwork. Resolved and accepted
// findings are finished, so a recurrence is raised as a new finding. And the
// audit closes only once nothing is live and a snapshot has been frozen, which
// is the audit's own record of the position it closed on.
export function GovAuditDetail() {
  const { auditProjectId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";

  const project = useAsync<AuditProject>(
    async () => (await auditApi.project(orgId, auditProjectId)).data.auditProject,
    [orgId, auditProjectId],
  );
  const scope = useAsync<AuditScopeEntry[]>(
    async () => (await auditApi.scope(orgId, auditProjectId)).data.scope,
    [orgId, auditProjectId],
  );
  const findings = useAsync<AuditFinding[]>(
    async () => (await auditApi.findings(orgId, auditProjectId)).data.findings,
    [orgId, auditProjectId],
  );
  const readiness = useAsync<ReadinessScore>(
    async () => (await auditApi.readiness(orgId, auditProjectId)).data.readiness,
    [orgId, auditProjectId],
  );
  const coverage = useAsync<CoverageRollup>(
    async () => (await auditApi.coverage(orgId, auditProjectId)).data.coverage,
    [orgId, auditProjectId],
  );
  const snapshots = useAsync<ReadinessSnapshot[]>(
    async () => (await auditApi.snapshots(orgId, auditProjectId)).data.snapshots,
    [orgId, auditProjectId],
  );
  const packages = useAsync<AuditPackageLink[]>(
    async () => (await auditApi.packages(orgId, auditProjectId)).data.packages,
    [orgId, auditProjectId],
  );
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);
  const memberName = (id: string | null) => {
    if (!id) return "Not recorded";
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const reloadGoverned = () => {
    findings.reload();
    readiness.reload();
    coverage.reload();
  };

  const row = project.data;
  const liveFindings = (findings.data ?? []).filter(
    (f) => f.status !== "resolved" && f.status !== "accepted",
  );

  return (
    <>
      <div className="page-head">
        <div>
          <p className="muted-note">
            <Link to="/studio/governance/audits">Audits</Link>
          </p>
          <h1>{row?.name ?? "Audit"}</h1>
          <p>
            {row?.description ||
              "Scope the examination, record what it found and freeze the readiness position it closed on."}
          </p>
          {row ? (
            <div className="badge-row">
              <ProjectStatusBadge status={row.status} />
              {row.closedAt ? <Badge tone="neutral">Closed {formatDate(row.closedAt)}</Badge> : null}
            </div>
          ) : null}
        </div>
        {row ? (
          <div className="badge-row">
            {NEXT_PROJECT_STATUS[row.status].map((next) => (
              <ProjectTransition
                key={next}
                orgId={orgId}
                project={row}
                next={next}
                liveFindings={liveFindings.length}
                snapshotCount={(snapshots.data ?? []).length}
                onDone={() => {
                  project.reload();
                  snapshots.reload();
                }}
              />
            ))}
          </div>
        ) : null}
      </div>

      <GovPanel title="Audit" state={project} loadingLabel="Loading audit…">
        {(data) => (
          <dl className="detail-grid">
            <div>
              <dt>Auditor</dt>
              <dd>{data.auditorName || "Not recorded"}</dd>
            </div>
            <div>
              <dt>Lead</dt>
              <dd>{memberName(data.leadId)}</dd>
            </div>
            <div>
              <dt>Period</dt>
              <dd>
                {data.periodStart ? formatDate(data.periodStart) : "Open start"} to{" "}
                {data.periodEnd ? formatDate(data.periodEnd) : "Open end"}
              </dd>
            </div>
            <div>
              <dt>Framework version</dt>
              <dd>
                {data.frameworkVersionId ? (
                  <Link to={`/studio/governance/frameworks/${data.frameworkVersionId}`}>
                    {data.frameworkVersionId}
                  </Link>
                ) : (
                  "Not set"
                )}
              </dd>
            </div>
            <div>
              <dt>Raised</dt>
              <dd>
                {formatDate(data.createdAt)} by {memberName(data.createdBy)}
              </dd>
            </div>
            <div>
              <dt>Closed</dt>
              <dd>
                {data.closedAt
                  ? `${formatDate(data.closedAt)} by ${memberName(data.closedBy)}`
                  : "Still open"}
              </dd>
            </div>
          </dl>
        )}
      </GovPanel>

      <ScopeSection
        orgId={orgId}
        auditProjectId={auditProjectId}
        scope={scope}
        frameworkVersionId={row?.frameworkVersionId ?? null}
        memberName={memberName}
        onChanged={() => {
          scope.reload();
          reloadGoverned();
        }}
      />

      <FindingsSection
        orgId={orgId}
        auditProjectId={auditProjectId}
        findings={findings}
        members={members.data ?? []}
        memberName={memberName}
        frameworkVersionId={row?.frameworkVersionId ?? null}
        onChanged={reloadGoverned}
      />

      <ReadinessSection
        orgId={orgId}
        auditProjectId={auditProjectId}
        readiness={readiness}
        onFrozen={snapshots.reload}
      />

      <CoverageSection coverage={coverage} />

      <SnapshotSection snapshots={snapshots} memberName={memberName} />

      <PackageSection
        orgId={orgId}
        auditProjectId={auditProjectId}
        packages={packages}
        memberName={memberName}
      />
    </>
  );
}

function ProjectTransition({
  orgId,
  project,
  next,
  liveFindings,
  snapshotCount,
  onDone,
}: {
  orgId: string;
  project: AuditProject;
  next: AuditProjectStatus;
  liveFindings: number;
  snapshotCount: number;
  onDone: () => void;
}) {
  // The gates below are the backend's, and it refuses regardless of what this
  // says. Stating them here means the person clicking Close knows what the
  // audit is being held to before the refusal arrives.
  const consequence =
    next === "closed"
      ? "An audit closes only when every finding is resolved or accepted and a readiness snapshot has been frozen. A closed audit accepts no further changes."
      : next === "cancelled"
        ? "A cancelled audit accepts no further changes."
        : undefined;

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.auditsManage}
      label={projectStatusLabel(next)}
      title={`Move this audit to ${projectStatusLabel(next).toLowerCase()}`}
      description={`The audit is ${projectStatusLabel(project.status).toLowerCase()} today.`}
      consequence={consequence}
      confirmLabel="Confirm"
      busyLabel="Saving…"
      destructive={next === "cancelled"}
      triggerVariant={next === "closed" ? "primary" : "secondary"}
      triggerSize="md"
      onConfirm={() => auditApi.setProjectStatus(orgId, project.id, next)}
      onDone={onDone}
    >
      {next === "closed" ? (
        <ul className="muted-note">
          <li>
            {liveFindings === 0
              ? "Every finding is resolved or accepted."
              : `${liveFindings} finding${liveFindings === 1 ? " is" : "s are"} still live.`}
          </li>
          <li>
            {snapshotCount === 0
              ? "No readiness snapshot has been frozen yet."
              : `${snapshotCount} readiness snapshot${snapshotCount === 1 ? "" : "s"} frozen.`}
          </li>
        </ul>
      ) : null}
    </GovernedAction>
  );
}

function ScopeSection({
  orgId,
  auditProjectId,
  scope,
  frameworkVersionId,
  memberName,
  onChanged,
}: {
  orgId: string;
  auditProjectId: string;
  scope: ReturnType<typeof useAsync<AuditScopeEntry[]>>;
  frameworkVersionId: string | null;
  memberName: (id: string | null) => string;
  onChanged: () => void;
}) {
  return (
    <GovPanel
      title="Scope"
      action={
        <AddScope
          orgId={orgId}
          auditProjectId={auditProjectId}
          frameworkVersionId={frameworkVersionId}
          onDone={onChanged}
        />
      }
      state={scope}
      loadingLabel="Loading scope…"
      isEmpty={(data) => data.length === 0}
      emptyTitle="Nothing is in scope yet"
      emptyDescription="Add the framework version this audit examines, then the requirements, business units and systems it covers."
    >
      {(data) => (
        <>
          <p className="muted-note">
            An excluded requirement leaves the readiness denominator, so an exclusion is a decision:
            it needs a reason, and the person and time are recorded against it.
          </p>
          <DataTable
            caption="Audit scope"
            columns={[
              {
                key: "ref",
                header: "In scope",
                render: (entry) => (
                  <>
                    <strong>{entry.label || entry.scopeRef}</strong>
                    <div className="badge-row">
                      <Badge tone="neutral">{scopeTypeLabel(entry.scopeType)}</Badge>
                      {entry.included ? null : <Badge tone="warning">Excluded</Badge>}
                      {isFreeReference(entry.scopeType) ? (
                        <Badge tone="neutral">Not validated by Studio</Badge>
                      ) : null}
                    </div>
                    <div className="muted-note">
                      <code>{entry.scopeRef}</code>
                    </div>
                  </>
                ),
              },
              {
                key: "exclusion",
                header: "Exclusion",
                render: (entry) =>
                  entry.included ? (
                    <span className="muted-note">Included</span>
                  ) : (
                    <>
                      {entry.exclusionRationale}
                      <div className="muted-note">
                        {memberName(entry.excludedBy)}
                        {entry.excludedAt ? ` · ${formatDate(entry.excludedAt)}` : null}
                      </div>
                    </>
                  ),
              },
              {
                key: "actions",
                header: "",
                render: (entry) =>
                  entry.included ? (
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.auditsManage}
                      label="Exclude"
                      title="Exclude this from the audit"
                      description="Excluded requirements are reported as excluded and leave the readiness denominator."
                      consequence="The reason you give is kept with your name and the time, and the auditor sees it."
                      confirmLabel="Exclude"
                      busyLabel="Excluding…"
                      requireRationale
                      rationaleLabel="Why is this out of scope?"
                      onConfirm={(rationale) =>
                        auditApi.excludeScope(orgId, auditProjectId, entry.id, rationale)
                      }
                      onDone={onChanged}
                    />
                  ) : (
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.auditsManage}
                      label="Reinstate"
                      title="Bring this back into scope"
                      description="Removes the exclusion, so the requirement counts towards readiness again."
                      confirmLabel="Reinstate"
                      busyLabel="Reinstating…"
                      onConfirm={() => auditApi.includeScope(orgId, auditProjectId, entry.id)}
                      onDone={onChanged}
                    />
                  ),
              },
            ]}
            rows={data}
          />
        </>
      )}
    </GovPanel>
  );
}

function AddScope({
  orgId,
  auditProjectId,
  frameworkVersionId,
  onDone,
}: {
  orgId: string;
  auditProjectId: string;
  frameworkVersionId: string | null;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [scopeType, setScopeType] = useState<AuditScopeType>("requirement");
  const [versionId, setVersionId] = useState("");
  const [requirementId, setRequirementId] = useState("");
  const [reference, setReference] = useState("");
  const [label, setLabel] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.auditsManage);
  const picksRecord = scopeType === "framework_version" || scopeType === "requirement";

  const versions = useAsync<{ version: FrameworkVersion; framework: ComplianceFramework }[]>(
    async () => {
      if (!open || !picksRecord) return [];
      const frameworks = (await frameworksApi.frameworks(orgId, { scope: "tenant" })).data
        .frameworks;
      return (
        await Promise.all(
          frameworks.map(async (framework) =>
            (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
              version,
              framework,
            })),
          ),
        )
      ).flat();
    },
    [orgId, open, picksRecord],
  );
  const version = versionId || frameworkVersionId || versions.data?.[0]?.version.id || "";
  const requirements = useAsync<FrameworkRequirement[]>(
    async () =>
      open && scopeType === "requirement" && version
        ? (await frameworksApi.requirements(orgId, version)).data.requirements
        : [],
    [orgId, version, open, scopeType],
  );
  const requirement = requirementId || requirements.data?.[0]?.id || "";

  const scopeRef =
    scopeType === "framework_version" ? version : scopeType === "requirement" ? requirement : reference.trim();

  const submit = async () => {
    const ok = await mutation.run(() =>
      auditApi.addScope(orgId, auditProjectId, {
        scopeType,
        scopeRef,
        label: label.trim(),
      }),
    );
    if (!ok) return;
    setOpen(false);
    setReference("");
    setLabel("");
    onDone();
  };

  return (
    <>
      <ActionGate permission={GOVERNANCE_PERMISSIONS.auditsManage}>
        <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
          Add to scope
        </Button>
      </ActionGate>
      <Dialog
        open={open}
        title="Add to the audit scope"
        description="Frameworks and requirements are picked from your governed records. Business units and systems are references you type."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!scopeRef || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Adding…" : "Add to scope"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Scope type">
          {(id) => (
            <Select
              id={id}
              value={scopeType}
              onChange={(e) => setScopeType(e.target.value as AuditScopeType)}
            >
              <option value="framework_version">Framework version</option>
              <option value="requirement">Requirement</option>
              <option value="business_unit">Business unit</option>
              <option value="system">System</option>
            </Select>
          )}
        </Field>

        {picksRecord ? (
          <Field label="Framework version">
            {(id) => (
              <Select id={id} value={version} onChange={(e) => setVersionId(e.target.value)}>
                {(versions.data ?? []).map(({ version: v, framework }) => (
                  <option key={v.id} value={v.id}>
                    {framework.name} {v.versionLabel}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {scopeType === "requirement" ? (
          <Field label="Requirement">
            {(id) => (
              <Select
                id={id}
                value={requirement}
                onChange={(e) => setRequirementId(e.target.value)}
              >
                {(requirements.data ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.requirementKey} {r.title}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {isFreeReference(scopeType) ? (
          <Field
            label={scopeType === "business_unit" ? "Business unit reference" : "System reference"}
            help="Studio holds no registry of business units or systems, so this is recorded exactly as you type it and nothing is checked against it."
          >
            {(id) => (
              <TextInput
                id={id}
                value={reference}
                placeholder={scopeType === "business_unit" ? "BU-PLATFORM" : "SYS-IDP"}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
        ) : null}

        <Field label="Label" help="How this reads in the scope list. Optional.">
          {(id) => <TextInput id={id} value={label} onChange={(e) => setLabel(e.target.value)} />}
        </Field>
      </Dialog>
    </>
  );
}

function FindingsSection({
  orgId,
  auditProjectId,
  findings,
  members,
  memberName,
  frameworkVersionId,
  onChanged,
}: {
  orgId: string;
  auditProjectId: string;
  findings: ReturnType<typeof useAsync<AuditFinding[]>>;
  members: Member[];
  memberName: (id: string | null) => string;
  frameworkVersionId: string | null;
  onChanged: () => void;
}) {
  const [status, setStatus] = useState("");
  const [history, setHistory] = useState<AuditFinding | null>(null);

  const shown = (findings.data ?? []).filter((f) => !status || f.status === status);

  return (
    <>
      <GovPanel
        title="Findings"
        action={
          <RaiseFinding
            orgId={orgId}
            auditProjectId={auditProjectId}
            members={members}
            frameworkVersionId={frameworkVersionId}
            onDone={onChanged}
          />
        }
        state={findings}
        loadingLabel="Loading findings…"
        isEmpty={() => shown.length === 0}
        emptyTitle="No findings match"
        emptyDescription="Raise what the examination found, with an owner and a date it is due by."
      >
        {() => (
          <>
            <p className="muted-note">
              Resolved and accepted findings are finished. If the same problem happens again it is
              raised as a new finding, so the record of what this audit found stays intact. During
              fieldwork the owner of a finding cannot close it themselves.
            </p>
            <div className="filter-row">
              <Field label="Status">
                {(id) => (
                  <Select id={id} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Any status</option>
                    {FINDING_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {findingStatusLabel(s)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <DataTable
              caption="Audit findings"
              columns={[
                {
                  key: "title",
                  header: "Finding",
                  render: (finding) => (
                    <>
                      <strong>{finding.title}</strong>
                      <div className="badge-row">
                        <Badge tone="neutral">{finding.findingKey}</Badge>
                        <FindingStatusBadge status={finding.status} />
                        <SeverityBadge severity={finding.severity} />
                      </div>
                      {finding.description ? (
                        <div className="muted-note">{finding.description}</div>
                      ) : null}
                    </>
                  ),
                },
                { key: "owner", header: "Owner", render: (f) => memberName(f.ownerId) },
                {
                  key: "target",
                  header: "Against",
                  render: (f) =>
                    f.requirementId ? (
                      <code>{f.requirementId}</code>
                    ) : f.policyId ? (
                      <code>{f.policyId}</code>
                    ) : (
                      <span className="muted-note">Not linked</span>
                    ),
                },
                {
                  key: "due",
                  header: "Due",
                  render: (f) =>
                    f.dueAt ? formatDate(f.dueAt) : <span className="muted-note">No due date</span>,
                },
                {
                  key: "decided",
                  header: "Settled",
                  render: (f) =>
                    f.decidedAt ? (
                      <>
                        {formatDate(f.decidedAt)}
                        <div className="muted-note">by {memberName(f.decidedBy)}</div>
                      </>
                    ) : (
                      <span className="muted-note">Still live</span>
                    ),
                },
                {
                  key: "actions",
                  header: "",
                  render: (finding) => (
                    <div className="badge-row">
                      <Button variant="ghost" size="sm" onClick={() => setHistory(finding)}>
                        History
                      </Button>
                      {NEXT_FINDING_STATUS[finding.status].map((next) => (
                        <GovernedAction
                          key={next}
                          permission={GOVERNANCE_PERMISSIONS.findingsDecide}
                          label={findingStatusLabel(next)}
                          title={`Move this finding to ${findingStatusLabel(next).toLowerCase()}`}
                          description={`${finding.findingKey} · ${finding.title}`}
                          consequence={
                            next === "resolved" || next === "accepted"
                              ? "This settles the finding. It cannot be moved again, and a recurrence is raised as a new finding."
                              : undefined
                          }
                          confirmLabel="Confirm"
                          busyLabel="Saving…"
                          requireRationale={next === "accepted" || next === "disputed"}
                          rationaleLabel="Rationale"
                          onConfirm={(rationale) =>
                            auditApi.setFindingStatus(
                              orgId,
                              finding.id,
                              next,
                              rationale || undefined,
                            )
                          }
                          onDone={onChanged}
                        />
                      ))}
                    </div>
                  ),
                },
              ]}
              rows={shown}
            />
          </>
        )}
      </GovPanel>

      {history ? (
        <FindingHistory
          orgId={orgId}
          finding={history}
          memberName={memberName}
          onClose={() => setHistory(null)}
        />
      ) : null}
    </>
  );
}

function FindingHistory({
  orgId,
  finding,
  memberName,
  onClose,
}: {
  orgId: string;
  finding: AuditFinding;
  memberName: (id: string | null) => string;
  onClose: () => void;
}) {
  const history = useAsync<FindingTransition[]>(
    async () => (await auditApi.findingHistory(orgId, finding.id)).data.history,
    [orgId, finding.id],
  );

  return (
    <Dialog
      open
      title={`${finding.findingKey} history`}
      description="Every transition, in the order it happened. Nothing here is edited or removed."
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <GovPanel state={history} loadingLabel="Loading history…" bare
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="Nothing recorded yet"
        emptyDescription="The finding has not moved since it was raised."
      >
        {(rows) => (
          <ol className="timeline">
            {rows.map((entry) => (
              <li key={entry.id}>
                <div className="badge-row">
                  <FindingStatusBadge status={entry.fromStatus} />
                  <span aria-hidden="true">→</span>
                  <FindingStatusBadge status={entry.toStatus} />
                </div>
                <div className="muted-note">
                  {memberName(entry.actorId)} · {formatDate(entry.decidedAt)}
                </div>
                {entry.rationale ? <p>{entry.rationale}</p> : null}
              </li>
            ))}
          </ol>
        )}
      </GovPanel>
    </Dialog>
  );
}

function RaiseFinding({
  orgId,
  auditProjectId,
  members,
  frameworkVersionId,
  onDone,
}: {
  orgId: string;
  auditProjectId: string;
  members: Member[];
  frameworkVersionId: string | null;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<FindingSeverity>("medium");
  const [ownerId, setOwnerId] = useState("");
  const [requirementId, setRequirementId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.auditsManage);

  const requirements = useAsync<FrameworkRequirement[]>(
    async () =>
      open && frameworkVersionId
        ? (await frameworksApi.requirements(orgId, frameworkVersionId)).data.requirements
        : [],
    [orgId, open, frameworkVersionId],
  );
  const owner = ownerId || members[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() =>
      auditApi.raiseFinding(orgId, auditProjectId, {
        title: title.trim(),
        description: description.trim(),
        severity,
        ownerId: owner,
        requirementId: requirementId || null,
        dueAt: dueAt || null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setTitle("");
    setDescription("");
    setDueAt("");
    onDone();
  };

  return (
    <>
      <ActionGate permission={GOVERNANCE_PERMISSIONS.auditsManage}>
        <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
          Raise finding
        </Button>
      </ActionGate>
      <Dialog
        open={open}
        title="Raise a finding"
        description="The finding opens against an owner. Settling it is a separate decision, and during fieldwork the owner cannot make it."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!title.trim() || !owner || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Raising…" : "Raise finding"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Title">
          {(id) => (
            <TextInput
              id={id}
              value={title}
              placeholder="Access reviews were not completed for the quarter"
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </Field>
        <Field label="What was found">
          {(id) => (
            <TextArea
              id={id}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label="Severity">
          {(id) => (
            <Select
              id={id}
              value={severity}
              onChange={(e) => setSeverity(e.target.value as FindingSeverity)}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {severityLabel(s)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Owner" help="Who is accountable for putting it right.">
          {(id) => (
            <Select id={id} value={owner} onChange={(e) => setOwnerId(e.target.value)}>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Requirement">
          {(id) => (
            <Select
              id={id}
              value={requirementId}
              onChange={(e) => setRequirementId(e.target.value)}
            >
              <option value="">Not linked to a requirement</option>
              {(requirements.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.requirementKey} {r.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Due">
          {(id) => (
            <TextInput
              id={id}
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          )}
        </Field>
      </Dialog>
    </>
  );
}

function ReadinessSection({
  orgId,
  auditProjectId,
  readiness,
  onFrozen,
}: {
  orgId: string;
  auditProjectId: string;
  readiness: ReturnType<typeof useAsync<ReadinessScore>>;
  onFrozen: () => void;
}) {
  const [frozen, setFrozen] = useState<{ created: boolean; inputsHash: string } | null>(null);

  return (
    <GovPanel
      title="Readiness"
      action={
        <GovernedAction
          permission={GOVERNANCE_PERMISSIONS.auditsManage}
          label="Freeze snapshot"
          title="Freeze this readiness position"
          description="Keeps the score, every component and the inputs hash exactly as they stand now."
          consequence="A snapshot is never edited or removed. Freezing an unchanged position returns the snapshot you already have rather than writing a second one."
          confirmLabel="Freeze"
          busyLabel="Freezing…"
          triggerVariant="primary"
          onConfirm={async () => {
            const res = await auditApi.freezeReadiness(orgId, auditProjectId);
            setFrozen({
              created: res.data.created,
              inputsHash: res.data.snapshot.inputsHash,
            });
            return res;
          }}
          onDone={onFrozen}
        />
      }
      state={readiness}
      loadingLabel="Computing readiness…"
    >
      {(score) => (
        <>
          <div className="metric-grid" data-testid="readiness-summary">
            <div className="metric-card">
              <Metric value={`${score.score}%`} label="Readiness" />
            </div>
            <div className="metric-card">
              <Metric value="Deterministic" label="How it was computed" />
            </div>
            <div className="metric-card">
              <Metric value={score.calculatorVersion} label="Calculator" />
            </div>
          </div>

          {frozen ? (
            <p className="muted-note" role="status">
              {frozen.created
                ? "Snapshot frozen."
                : "Nothing governed has changed, so the existing snapshot was returned rather than a second one written."}{" "}
              Inputs hash <code>{frozen.inputsHash}</code>
            </p>
          ) : null}

          <p className="muted-note">
            Readiness is counted from governed records alone. AI confidence never moves it, and a
            mapping a person has not verified counts as uncovered. Where a component had nothing in
            scope to measure, the backend left it out and gave its weight to the components that did
            apply. Nothing on this page recalculates any of that.
          </p>

          <DataTable
            caption="Readiness components"
            columns={[
              {
                key: "component",
                header: "Component",
                render: (component: ReadinessComponent) => (
                  <>
                    <strong>{component.label}</strong>
                    <div className="badge-row">
                      {component.included ? (
                        <Badge tone="info">Counted</Badge>
                      ) : (
                        <Badge tone="neutral">Not applicable, omitted</Badge>
                      )}
                    </div>
                    <div className="muted-note">{component.detail}</div>
                  </>
                ),
              },
              {
                key: "counted",
                header: "Counted",
                render: (component: ReadinessComponent) =>
                  component.included ? (
                    <>
                      {component.numerator} of {component.denominator}
                      <div className="muted-note">{componentRatioLabel(component)}</div>
                    </>
                  ) : (
                    <>
                      <span className="muted-note">Nothing in scope to measure</span>
                      <div className="muted-note">Not applicable</div>
                    </>
                  ),
              },
              {
                key: "weight",
                header: "Weight",
                render: (component: ReadinessComponent) => percent(component.weight),
              },
              {
                key: "effective",
                header: "Effective weight",
                render: (component: ReadinessComponent) =>
                  component.included ? (
                    <>
                      {percent(component.effectiveWeight, 2)}
                      {component.effectiveWeight > component.weight ? (
                        <div className="muted-note">Raised by the backend redistribution</div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      0%
                      <div className="muted-note">
                        Weight redistributed by the backend to the counted components
                      </div>
                    </>
                  ),
              },
            ]}
            rows={score.components.map((component) => ({ ...component, id: component.key }))}
          />

          <dl className="detail-grid">
            <div>
              <dt>Inputs hash</dt>
              <dd>
                <code>{score.inputsHash}</code>
              </dd>
            </div>
            <div>
              <dt>Computed</dt>
              <dd>{formatDate(score.generatedAt)}</dd>
            </div>
            <div>
              <dt>Excluded requirements</dt>
              <dd>
                {score.excludedRequirementIds.length === 0
                  ? "None"
                  : score.excludedRequirementIds.map((id) => (
                      <div key={id}>
                        <code>{id}</code>
                      </div>
                    ))}
              </dd>
            </div>
          </dl>
        </>
      )}
    </GovPanel>
  );
}

function CoverageSection({
  coverage,
}: {
  coverage: ReturnType<typeof useAsync<CoverageRollup>>;
}) {
  return (
    <GovPanel
      title="Framework coverage"
      state={coverage}
      loadingLabel="Loading coverage…"
      isEmpty={(data) => data.rows.length === 0}
      emptyTitle="Nothing to cover yet"
      emptyDescription="Add a framework version to the scope and the requirements appear here."
    >
      {(data) => (
        <>
          <p className="muted-note">
            {data.inScopeCount} requirement{data.inScopeCount === 1 ? "" : "s"} in scope,{" "}
            {data.excludedCount} excluded. A requirement counts as covered by policy only where a
            person verified the mapping.
          </p>
          <DataTable
            caption="Requirement coverage"
            columns={[
              {
                key: "requirement",
                header: "Requirement",
                render: (row) => (
                  <>
                    <strong>{row.requirementKey}</strong> {row.title}
                  </>
                ),
              },
              {
                key: "policy",
                header: "Policy",
                render: (row) =>
                  row.policyCovered ? (
                    <Badge tone="success">Covered</Badge>
                  ) : (
                    <Badge tone="warning">Uncovered</Badge>
                  ),
              },
              {
                key: "evidence",
                header: "Evidence",
                render: (row) => (
                  <div className="badge-row">
                    {row.evidenceCovered ? (
                      <Badge tone="success">Held</Badge>
                    ) : (
                      <Badge tone="warning">None</Badge>
                    )}
                    {row.evidenceCovered && !row.evidenceFresh ? (
                      <Badge tone="warning">Past review</Badge>
                    ) : null}
                  </div>
                ),
              },
              {
                key: "findings",
                header: "Open findings",
                render: (row) =>
                  row.openFindings === 0 ? (
                    <span className="muted-note">None</span>
                  ) : (
                    <Badge tone="danger">{row.openFindings}</Badge>
                  ),
              },
            ]}
            rows={data.rows.map((row) => ({ ...row, id: row.requirementId }))}
          />
        </>
      )}
    </GovPanel>
  );
}

function SnapshotSection({
  snapshots,
  memberName,
}: {
  snapshots: ReturnType<typeof useAsync<ReadinessSnapshot[]>>;
  memberName: (id: string | null) => string;
}) {
  const [shown, setShown] = useState<ReadinessSnapshot | null>(null);

  return (
    <>
      <GovPanel
        title="Frozen readiness snapshots"
        state={snapshots}
        loadingLabel="Loading snapshots…"
        isEmpty={(data) => data.length === 0}
        emptyTitle="No snapshot frozen yet"
        emptyDescription="Freeze the readiness position to keep a record of where the audit stood. One is required before the audit can close."
      >
        {(data) => (
          <>
            <p className="muted-note">
              A snapshot keeps the position it was frozen on. Later work changes the live readiness
              above and produces a new snapshot; it never rewrites an earlier one.
            </p>
            <DataTable
              caption="Frozen readiness snapshots"
              columns={[
                {
                  key: "score",
                  header: "Score",
                  render: (snapshot) => (
                    <>
                      <strong>{snapshot.score}%</strong>
                      <div className="muted-note">Calculator {snapshot.calculatorVersion}</div>
                    </>
                  ),
                },
                {
                  key: "hash",
                  header: "Inputs hash",
                  render: (snapshot) => <code>{snapshot.inputsHash}</code>,
                },
                {
                  key: "frozen",
                  header: "Frozen",
                  render: (snapshot) => (
                    <>
                      {formatDate(snapshot.generatedAt)}
                      <div className="muted-note">by {memberName(snapshot.generatedBy)}</div>
                    </>
                  ),
                },
                {
                  key: "actions",
                  header: "",
                  render: (snapshot) => (
                    <Button variant="ghost" size="sm" onClick={() => setShown(snapshot)}>
                      Components
                    </Button>
                  ),
                },
              ]}
              rows={data}
            />
          </>
        )}
      </GovPanel>

      {shown ? (
        <Dialog
          open
          title={`Snapshot at ${shown.score}%`}
          description="The components exactly as they were counted when this position was frozen."
          onClose={() => setShown(null)}
          footer={
            <Button variant="ghost" onClick={() => setShown(null)}>
              Close
            </Button>
          }
        >
          <DataTable
            caption="Snapshot components"
            columns={[
              { key: "label", header: "Component", render: (c: ReadinessComponent) => c.label },
              {
                key: "counted",
                header: "Counted",
                render: (c: ReadinessComponent) =>
                  c.included ? `${c.numerator} of ${c.denominator}` : "Not applicable",
              },
              { key: "weight", header: "Weight", render: (c: ReadinessComponent) => percent(c.weight) },
              {
                key: "effective",
                header: "Effective weight",
                render: (c: ReadinessComponent) => percent(c.effectiveWeight, 2),
              },
            ]}
            rows={shown.components.map((c) => ({ ...c, id: c.key }))}
          />
          <p className="muted-note">
            Inputs hash <code>{shown.inputsHash}</code>
          </p>
        </Dialog>
      ) : null}
    </>
  );
}

function PackageSection({
  orgId,
  auditProjectId,
  packages,
  memberName,
}: {
  orgId: string;
  auditProjectId: string;
  packages: ReturnType<typeof useAsync<AuditPackageLink[]>>;
  memberName: (id: string | null) => string;
}) {
  const available = useAsync<EvidencePackage[]>(
    async () => (await evidenceApi.packages(orgId)).data.packages,
    [orgId],
  );
  const packageName = (id: string) =>
    (available.data ?? []).find((p) => p.id === id)?.name ?? id;

  return (
    <GovPanel
      title="Evidence handed over"
      action={
        <LinkPackage
          orgId={orgId}
          auditProjectId={auditProjectId}
          available={available.data ?? []}
          linked={(packages.data ?? []).map((p) => p.evidencePackageId)}
          onDone={packages.reload}
        />
      }
      state={packages}
      loadingLabel="Loading packages…"
      isEmpty={(data) => data.length === 0}
      emptyTitle="No package linked yet"
      emptyDescription="Link a package Evidence has already frozen. Packages are built there, not here."
    >
      {(data) => (
        <>
          <p className="muted-note">
            The manifest hash and item count are captured when the package is linked, so the audit
            records the package as it stood at that moment.{" "}
            <Link to="/studio/governance/evidence/packages">Evidence packages</Link>
          </p>
          <DataTable
            caption="Linked evidence packages"
            columns={[
              {
                key: "name",
                header: "Package",
                render: (link) => (
                  <>
                    <strong>{packageName(link.evidencePackageId)}</strong>
                    <div className="muted-note">
                      <code>{link.evidencePackageId}</code>
                    </div>
                  </>
                ),
              },
              {
                key: "hash",
                header: "Manifest hash",
                render: (link) => <code>{link.manifestHash}</code>,
              },
              { key: "items", header: "Items", render: (link) => link.itemCount },
              {
                key: "linked",
                header: "Linked",
                render: (link) => (
                  <>
                    {formatDate(link.linkedAt)}
                    <div className="muted-note">by {memberName(link.linkedBy)}</div>
                  </>
                ),
              },
            ]}
            rows={data}
          />
        </>
      )}
    </GovPanel>
  );
}

function LinkPackage({
  orgId,
  auditProjectId,
  available,
  linked,
  onDone,
}: {
  orgId: string;
  auditProjectId: string;
  available: EvidencePackage[];
  linked: string[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [packageId, setPackageId] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.auditsManage);

  const choices = available.filter((p) => !linked.includes(p.id));
  const chosen = packageId || choices[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() => auditApi.linkPackage(orgId, auditProjectId, chosen));
    if (!ok) return;
    setOpen(false);
    setPackageId("");
    onDone();
  };

  return (
    <>
      <ActionGate permission={GOVERNANCE_PERMISSIONS.auditsManage}>
        <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
          Link package
        </Button>
      </ActionGate>
      <Dialog
        open={open}
        title="Link an evidence package"
        description="Packages are frozen in Evidence. Linking records the one you handed to the auditor."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!chosen || mutation.busy} onClick={() => void submit()}>
              {mutation.busy ? "Linking…" : "Link package"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        {choices.length === 0 ? (
          <p className="muted-note">
            Every package you have is already linked. Freeze another in{" "}
            <Link to="/studio/governance/evidence/packages">Evidence packages</Link>.
          </p>
        ) : (
          <Field label="Package">
            {(id) => (
              <Select id={id} value={chosen} onChange={(e) => setPackageId(e.target.value)}>
                {choices.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.itemCount} item{p.itemCount === 1 ? "" : "s"}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </Dialog>
    </>
  );
}
